/**
 * MCP tool: yap_annotate_plan (D2) — one-call exterior annotation over the
 * A5 dimension payloads: an exterior dimension chain (per wall segment) plus
 * the overall dimension on the wall envelope's long axis. Level labels are
 * rendered by the datum layer itself, so they need no elements.
 */

import { useBIMStore } from '../../state/useBIMStore';
import type { WallElement } from '../../core/types';
import type { McpToolHandler } from './mcpResultUtils';
import { asNumber, asString, okJson } from './mcpResultUtils';
import { handleCreateElement } from './mcpSceneTools';

interface Run {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

/** One dimension element through the regular create path (same contract errors). */
async function createDimension(name: string, refs: [number, number, number][], offset: number): Promise<{ id: string } | { error: string }> {
    const args: Record<string, unknown> = {
        category: 'Dimensions',
        name,
        refs,
        offset,
        geometryData: undefined,
    };
    // The sign of the offset decides which side of the refs the dim line sits
    // on; the perpendicular-shift builder only accepts positive offsets, so
    // outward-facing lines are written directly here with the same payload.
    const [a, b] = refs;
    const runX = b[0] - a[0];
    const runY = b[1] - a[1];
    const len = Math.hypot(runX, runY) || 1;
    const nx = -runY / len;
    const ny = runX / len;
    const shift = (p: [number, number, number]): [number, number, number] => [p[0] + nx * offset, p[1] + ny * offset, p[2]];
    args.geometryData = { kind: 'aligned', refs, dimStart: shift(a), dimEnd: shift(b) };
    args.offset = Math.abs(offset);
    const result = await handleCreateElement(args);
    if (!result.ok) return { error: result.error ?? 'dimension creation failed' };
    return { id: (result.data as { id: string }).id };
}

export const handleAnnotatePlan: McpToolHandler = async (args) => {
    const project = useBIMStore.getState().project;
    const walls = Array.from(project.elements.values()).filter(
        (e): e is WallElement => e.category === 'Walls',
    );
    const runs: Run[] = [];
    for (const wall of walls) {
        const g = wall.geometryData;
        if (!Array.isArray(g?.start) || !Array.isArray(g?.end)) continue;
        runs.push({ x1: g.start[0], y1: g.start[1], x2: g.end[0], y2: g.end[1] });
    }
    if (runs.length < 2) {
        return {
            ok: false,
            error: 'Not enough walls to annotate — create the envelope first (at least two walls with real geometry).',
        };
    }
    const pad = Math.max(asNumber(args.offset) ?? 0.8, 0.1);
    const minX = Math.min(...runs.flatMap((r) => [r.x1, r.x2]));
    const maxX = Math.max(...runs.flatMap((r) => [r.x1, r.x2]));
    const minY = Math.min(...runs.flatMap((r) => [r.y1, r.y2]));
    const maxY = Math.max(...runs.flatMap((r) => [r.y1, r.y2]));
    const z = 0;
    const created: string[] = [];
    const failed: string[] = [];
    const push = async (name: string, refs: [number, number, number][], offset: number) => {
        const result = await createDimension(name, refs, offset);
        if ('id' in result) created.push(name);
        else failed.push(`${name}: ${result.error}`);
    };

    // Exterior segment chains: every wall lying on an envelope side contributes
    // its run; consecutive corner points on that side form the chain.
    const side = (on: 'south' | 'north' | 'west' | 'east'): [number, number][] => {
        const pts: [number, number][] = [];
        for (const r of runs) {
            const horizontal = Math.abs(r.y1 - r.y2) < 1e-3;
            const vertical = Math.abs(r.x1 - r.x2) < 1e-3;
            if (on === 'south' && horizontal && Math.abs(r.y1 - minY) < 1e-3) pts.push([r.x1, r.y1], [r.x2, r.y2]);
            if (on === 'north' && horizontal && Math.abs(r.y1 - maxY) < 1e-3) pts.push([r.x1, r.y1], [r.x2, r.y2]);
            if (on === 'west' && vertical && Math.abs(r.x1 - minX) < 1e-3) pts.push([r.x1, r.y1], [r.x2, r.y2]);
            if (on === 'east' && vertical && Math.abs(r.x1 - maxX) < 1e-3) pts.push([r.x1, r.y1], [r.x2, r.y2]);
        }
        const key = (p: [number, number]) => `${p[0].toFixed(3)},${p[1].toFixed(3)}`;
        const unique = Array.from(new Map(pts.map((p) => [key(p), p])).values());
        return unique.sort((a, b) => (on === 'south' || on === 'north' ? a[0] - b[0] : a[1] - b[1]));
    };

    const chainSign = { south: -1, north: 1, west: -1, east: 1 } as const;
    const sides: Array<['south' | 'north' | 'west' | 'east', number]> = [
        ['south', chainSign.south],
        ['west', chainSign.west],
    ];
    for (const [name, sign] of sides) {
        const pts = side(name);
        if (pts.length < 2) continue;
        for (let i = 0; i < pts.length - 1; i++) {
            const a = pts[i];
            const b = pts[i + 1];
            if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 0.05) continue;
            push(
                `${name[0].toUpperCase()}${name.slice(1)} Dim ${i + 1}`,
                [[a[0], a[1], z], [b[0], b[1], z]],
                sign * pad,
            );
        }
        // Overall dimension on the far side of the chain.
        const first = pts[0];
        const last = pts[pts.length - 1];
        push(`${name[0].toUpperCase()}${name.slice(1)} Overall`, [[first[0], first[1], z], [last[0], last[1], z]], sign * (pad + 0.6));
    }

    return okJson(
        { created: created.length, failed, names: created },
        created.length > 0
            ? `Annotated the plan: ${created.length} exterior dimension(s) created${failed.length ? `, ${failed.length} failed` : ''}.`
            : 'No dimension chains could be derived from the wall geometry.',
    );
};
